import React, { useEffect } from 'react';
import { AppProvider, useApp } from './context/AppContext';
import { SyncProvider } from './context/SyncContext';
import apiService from './services/apiService';
import Sidebar from './components/layout/Sidebar';
import Header from './components/layout/Header';
import SyncModal from './components/sync/SyncModal';
import LoginPage from './pages/auth/LoginPage';
import SalesPage from './pages/sales/SalesPage';
import CounterSalesPage from './pages/sales/CounterSalesPage';
import CounterSalesPageRefactored from './pages/sales/CounterSalesPageRefactored';
import InventoryPage from './pages/inventory/InventoryPage';
import ProductManagementPage from './pages/inventory/ProductManagementPage';
import MigracionProductosPage from './pages/inventory/MigracionProductosPage';
import CambioPreciosPage from './pages/inventory/CambioPreciosPage';
import PreciosRayPage from './pages/inventory/PreciosRayPage';
import CustomerManagementPage from './pages/customers/CustomerManagementPage';
import AlmacenesPage from './pages/warehouses/AlmacenesPage';
import UsersPage from './pages/users/UsersPage';
import RolesPage from './pages/roles/RolesPage';
import FleteManagementPage from './pages/flete/FleteManagementPage';
import SalesReportPage from './pages/reports/SalesReportPage';
import ReporteVentasMostradorPage from './pages/reports/ReporteVentasMostradorPage';
import MedidoHoyPage from './pages/medidohoy/MedidoHoyPage';
import PickingPage from './pages/picking/PickingPage';
import PreparacionPage from './pages/almacen/PreparacionPage';
import ProveedoresPage from './pages/proveedores/ProveedoresPage';
import OrdenesCompraPage from './pages/compras/OrdenesCompraPage';
import CuentasPorPagarPage from './pages/compras/CuentasPorPagarPage';
import CuentasPorCobrarPage from './pages/cxc/CuentasPorCobrarPage';
import CaratulaPedidosPage from './pages/caratula/CaratulaPedidosPage';
import CorteCajaPage from './pages/cortes/CorteCajaPage';
import SettingsPage from './pages/settings/SettingsPage';
import FacturacionPage from './pages/facturacion/FacturacionPage';
import EtiquetasZebraPage from './pages/etiquetas/EtiquetasZebraPage';
import DevolucionesPage from './pages/devoluciones/DevolucionesPage';
import ApiTest from './components/debug/ApiTest';
import './styles/globals.css';
import './App.css';

// Componente principal de la aplicación
const MainApp = () => {
  const { isAuthenticated, currentView, setView, user } = useApp();

  // Auto-conectar báscula al iniciar (si hay puerto configurado)
  useEffect(() => {
    if (!isAuthenticated || !window.electron?.ipcRenderer) return;

    const autoConnectScale = async () => {
      try {
        // Verificar si ya está conectada
        const status = await window.electron.ipcRenderer.invoke('scale:status');
        if (status.connected) return;

        // Obtener config de báscula del servidor
        const nombrePc = localStorage.getItem('pos_nombre_pc');
        if (!nombrePc) return;

        const result = await apiService.getConfiguracionesPorCategoria('caja', nombrePc);
        if (!result.success || !result.data?.valores) return;

        const puerto = result.data.valores.caja_bascula_puerto;
        const baudRate = parseInt(result.data.valores.caja_bascula_baudrate) || 9600;
        if (!puerto) return;

        const conn = await window.electron.ipcRenderer.invoke('scale:connect', {
          path: puerto,
          baudRate: baudRate
        });
        if (conn.success) {
          console.log('Báscula auto-conectada:', puerto, '@', baudRate);
        }
      } catch (e) {
        console.warn('Auto-conexión báscula falló:', e.message);
      }
    };

    autoConnectScale();
  }, [isAuthenticated]);

  console.log('🔍 MainApp render - isAuthenticated:', isAuthenticated, 'user:', user);

  // Si no está autenticado, mostrar login
  if (!isAuthenticated) {
    console.log('🔐 Mostrando LoginPage porque isAuthenticated es false');
    return <LoginPage />;
  }

  // Mapeo de vistas a componentes (solo accesible si está autenticado)
  const views = {
    sales: { component: SalesPage, title: 'Punto de Venta' },
    'ventas-mostrador': { component: CounterSalesPageRefactored, title: 'Ventas de Mostrador' },
    //   'ventas-mostrador-v2': { component: CounterSalesPageRefactored, title: 'Ventas Mostrador (V2)' },
    'corte-caja': { component: CorteCajaPage, title: 'Corte de Caja' },
    'devoluciones': { component: DevolucionesPage, title: 'Devoluciones' },
    pedidos: { component: SalesPage, title: 'Pedidos' },
    inventory: { component: InventoryPage, title: 'Inventario' },
    products: { component: ProductManagementPage, title: 'Gestión de Productos' },
    'migracion-productos': { component: MigracionProductosPage, title: 'Migración de Productos' },
    'cambio-precios': { component: CambioPreciosPage, title: 'Cambio Rápido de Precios' },
    'precios-ray': { component: PreciosRayPage, title: 'Precios Ray' },
    categories: { component: () => <div className="page-placeholder">Categorías - En desarrollo</div>, title: 'Categorías' },
    suppliers: { component: ProveedoresPage, title: 'Proveedores' },
    proveedores: { component: ProveedoresPage, title: 'Proveedores' },
    'ordenes-compra': { component: OrdenesCompraPage, title: 'Órdenes de Compra' },
    'cuentas-por-pagar': { component: CuentasPorPagarPage, title: 'Cuentas por Pagar' },
    'cuentas-por-cobrar': { component: CuentasPorCobrarPage, title: 'Cuentas por Cobrar' },
    customers: { component: CustomerManagementPage, title: 'Gestión de Clientes' },
    catalogos: { component: () => <div className="page-placeholder">Catálogos - En desarrollo</div>, title: 'Catálogos' },
    almacenes: { component: AlmacenesPage, title: 'Gestión de Almacenes' },
    almacen: { component: () => <div className="page-placeholder">Almacén - En desarrollo</div>, title: 'Almacén' },
    picking: { component: PickingPage, title: 'Picking' },
    preparacion: { component: PreparacionPage, title: 'Preparación' },
    logistica: { component: () => <div className="page-placeholder">Logística - En desarrollo</div>, title: 'Logística' },
    'medido-hoy': { component: MedidoHoyPage, title: 'Pedidos de Hoy' },
    'caratula-pedidos': { component: CaratulaPedidosPage, title: 'Carátula de Pedidos' },
    flete: { component: FleteManagementPage, title: 'Gestión de Flete' },
    users: { component: UsersPage, title: 'Gestión de Usuarios' },
    roles: { component: RolesPage, title: 'Gestión de Roles' },
    reports: { component: SalesReportPage, title: 'Reporte de Ventas' },
    'reporte-ventas-mostrador': { component: ReporteVentasMostradorPage, title: 'Reporte Ventas Mostrador' },
    settings: { component: SettingsPage, title: 'Configuración' },
    facturacion: { component: FacturacionPage, title: 'Facturación CFDI' },
    'etiquetas-zebra': { component: EtiquetasZebraPage, title: 'Etiquetas Zebra' }
  };

  const CurrentView = views[currentView]?.component || SalesPage;
  const currentTitle = views[currentView]?.title || 'Dashboard';

  return (
    <SyncProvider isAuthenticated={isAuthenticated}>
      <div className="app-layout">
        <Sidebar activeView={currentView} onViewChange={setView} />
        <div className="app-main">
          <Header title={currentTitle} />
          <main className="app-content">
            <CurrentView />
          </main>
        </div>
        <ApiTest />
      </div>
      <SyncModal />
    </SyncProvider>
  );
};

// Componente App con Provider
function App() {
  return (
    <AppProvider>
      <MainApp />
    </AppProvider>
  );
}

export default App;
